import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from "react";

export type QuoteItem = {
  ref: string;
  name: string;
  brand?: string;
  image?: string | null;
  color?: string;
  sizes: Record<string, number>;
};

type Ctx = {
  items: QuoteItem[];
  count: number;
  totalQuantity: number;
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  addItem: (item: Omit<QuoteItem, "sizes"> & { sizes: Record<string, number> }) => void;
  removeItem: (ref: string, color?: string) => void;
  updateSizeQuantity: (ref: string, color: string | undefined, size: string, quantity: number) => void;
  clear: () => void;
};

const STORAGE_KEY = "mp_quote_cart_v2";

const QuoteCartContext = createContext<Ctx | null>(null);

const sameItem = (a: QuoteItem, ref: string, color?: string) =>
  a.ref === ref && (a.color ?? "") === (color ?? "");

const sumSizes = (sizes: Record<string, number>) =>
  Object.values(sizes).reduce((s, n) => s + (Number(n) || 0), 0);

export function QuoteCartProvider({ children }: { children: ReactNode }) {
  // Always start empty so the initial hydration render matches the prerendered
  // HTML snapshot exactly. Rehydrate from localStorage in an effect below.
  const [items, setItems] = useState<QuoteItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const hydratedRef = useRef(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setItems(JSON.parse(raw) as QuoteItem[]);
    } catch {
      /* ignore */
    }
    hydratedRef.current = true;
  }, []);

  useEffect(() => {
    // Skip the initial write so we don't clobber persisted items before the
    // rehydration effect above has had a chance to run.
    if (!hydratedRef.current) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      /* ignore */
    }
  }, [items]);

  const addItem: Ctx["addItem"] = useCallback((item) => {
    const cleanSizes: Record<string, number> = {};
    for (const [k, v] of Object.entries(item.sizes)) {
      const n = Number(v);
      if (n > 0) cleanSizes[k] = n;
    }
    if (Object.keys(cleanSizes).length === 0) return;

    setItems((prev) => {
      const idx = prev.findIndex((p) => sameItem(p, item.ref, item.color));
      if (idx >= 0) {
        const next = [...prev];
        const merged = { ...next[idx].sizes };
        for (const [k, v] of Object.entries(cleanSizes)) {
          merged[k] = (merged[k] ?? 0) + v;
        }
        next[idx] = { ...next[idx], sizes: merged };
        return next;
      }
      return [...prev, { ...item, sizes: cleanSizes }];
    });
    setIsOpen(true);
  }, []);

  const removeItem: Ctx["removeItem"] = useCallback((ref, color) => {
    setItems((prev) => prev.filter((p) => !sameItem(p, ref, color)));
  }, []);

  const updateSizeQuantity: Ctx["updateSizeQuantity"] = useCallback((ref, color, size, quantity) => {
    setItems((prev) =>
      prev
        .map((p) => {
          if (!sameItem(p, ref, color)) return p;
          const nextSizes = { ...p.sizes };
          if (quantity <= 0) delete nextSizes[size];
          else nextSizes[size] = quantity;
          return { ...p, sizes: nextSizes };
        })
        .filter((p) => Object.keys(p.sizes).length > 0),
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const value = useMemo<Ctx>(
    () => ({
      items,
      count: items.length,
      totalQuantity: items.reduce((s, i) => s + sumSizes(i.sizes), 0),
      isOpen,
      open: () => setIsOpen(true),
      close: () => setIsOpen(false),
      toggle: () => setIsOpen((v) => !v),
      addItem,
      removeItem,
      updateSizeQuantity,
      clear,
    }),
    [items, isOpen, addItem, removeItem, updateSizeQuantity, clear],
  );

  return <QuoteCartContext.Provider value={value}>{children}</QuoteCartContext.Provider>;
}

export function useQuoteCart() {
  const ctx = useContext(QuoteCartContext);
  if (!ctx) throw new Error("useQuoteCart must be used within QuoteCartProvider");
  return ctx;
}

export const sumQuoteItemSizes = sumSizes;
